import type { ReactNode } from 'react';

/**
 * El lienzo de las pantallas **sin sesión** (`/login` y `/sin-acceso`).
 *
 * El mundo visual sale del **isotipo**: esa "C" isométrica hecha de cintas azules y
 * verdes. Acá esas cintas se llevan a escala arquitectónica y cruzan un lienzo azul
 * casi negro, fuera de cuadro, en `mix-blend-screen` —sobre oscuro los degradés de
 * marca se suman a la luz en lugar de apagarse—, sobre una malla isométrica que se
 * desvanece con una máscara radial.
 *
 * Vive en un componente aparte porque las dos pantallas tienen que ser la misma
 * pieza: quien rebota en `/sin-acceso` viene de `/login`, y cualquier diferencia se
 * lee como que cambió de sitio. Cada pantalla aporta solo su contenido.
 *
 * Cuatro reglas que lo sostienen:
 *
 * 1. **Fija su propia paleta**: no lleva variantes `dark:`. Quien está acá todavía
 *    no tiene sesión, ni eligió tema. El logo va en su versión blanca; la de texto
 *    negro es ilegible sobre este fondo.
 * 2. **El texto nunca se apoya en una cinta**: un velo que se abre hacia la derecha
 *    hunde el lado izquierdo del lienzo. No es un parche de contraste, es lo que
 *    hace asimétrica la composición.
 * 3. **Ocupa exactamente el viewport** (`100dvh`) y no scrollea. Si una pantalla
 *    necesita mostrar una lista larga, le pone scroll propio a la lista.
 * 4. **Movimiento, no animación**: las cintas derivan en 34 s, imperceptible; la
 *    única entrada es la del bloque de contenido. Todo cae con `motion-reduce`.
 */

/**
 * Las cintas del fondo. Los colores son los del isotipo —azul profundo a celeste,
 * verde a lima— y el par `rotate/skew` es lo que las pone en la misma perspectiva
 * isométrica que el logo. Las dos últimas se esconden en teléfonos: a 375 px de
 * ancho llenarían el fondo en lugar de sugerirlo.
 *
 * Las opacidades son las del lienzo oscuro y van con `mix-blend-screen`: sobre el
 * azul casi negro estos valores se leen como haces de luz. En un fondo claro las
 * mismas cifras serían manchas.
 */
const CINTAS = [
  {
    id: 'azul-mayor',
    clase:
      '-left-48 -top-32 h-[36rem] w-[10rem] -rotate-[30deg] skew-y-[30deg] opacity-[0.42] blur-[1px]',
    de: '#0a5fa8',
    a: '#31b3e8',
  },
  {
    id: 'lima-mayor',
    clase:
      '-right-40 -bottom-40 h-[32rem] w-[8.5rem] rotate-[30deg] -skew-y-[30deg] opacity-[0.36] blur-[1px]',
    de: '#1f9e4a',
    a: '#b5cf2a',
  },
  {
    id: 'celeste-fina',
    clase:
      'left-[14%] -bottom-52 hidden h-[30rem] w-[3.25rem] -rotate-[30deg] skew-y-[30deg] opacity-[0.30] sm:block',
    de: '#1173b9',
    a: '#57b4e7',
  },
  {
    id: 'azul-alta',
    clase:
      'right-[18%] -top-56 hidden h-[28rem] w-[4.5rem] rotate-[30deg] -skew-y-[30deg] opacity-[0.24] blur-[2px] lg:block',
    de: '#0f4a70',
    a: '#1e87cf',
  },
] as const;

export default function LienzoMarca({ children }: { children: ReactNode }) {
  // `[height:100dvh]` + `overflow-hidden`: la pantalla es exactamente el viewport y
  // no scrollea nunca. En móviles `dvh` descuenta la barra de direcciones, y
  // `h-screen` queda como fallback para navegadores sin soporte.
  return (
    <main className="relative isolate grid h-screen [height:100dvh] items-center overflow-hidden bg-[#05192b] px-7 py-10 sm:px-12 lg:px-20 xl:px-28">
      {/* Luz de marca: azul entrando por arriba a la izquierda, verde saliendo por
          abajo a la derecha. Es lo que evita que el oscuro sea un agujero negro. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-30 bg-[image:radial-gradient(46rem_34rem_at_8%_-12%,#0e4f7c_0%,transparent_64%),radial-gradient(42rem_30rem_at_96%_112%,#11533a_0%,transparent_60%)]"
      />

      {/* Malla isométrica: dos familias de líneas a ±30°, apagadas por una máscara
          radial para que solo exista alrededor del contenido. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20 bg-[image:repeating-linear-gradient(30deg,rgba(143,198,233,0.10)_0_1px,transparent_1px_3.5rem),repeating-linear-gradient(150deg,rgba(143,198,233,0.10)_0_1px,transparent_1px_3.5rem)] [-webkit-mask-image:radial-gradient(68%_58%_at_50%_42%,#000_0%,transparent_78%)] [mask-image:radial-gradient(68%_58%_at_50%_42%,#000_0%,transparent_78%)]"
      />

      {/* Las cintas del isotipo, a escala de edificio y fuera de cuadro. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 animate-deriva mix-blend-screen motion-reduce:animate-none"
      >
        {CINTAS.map((c) => (
          <div
            key={c.id}
            className={`absolute rounded-sm ${c.clase}`}
            style={{ backgroundImage: `linear-gradient(180deg, ${c.de}, ${c.a})` }}
          />
        ))}
      </div>

      {/* Velo que hunde el lado izquierdo: sostiene el texto sin encerrarlo en una
          caja y deja el derecho iluminado. Va encima de las cintas (-z-[5]) y
          debajo del contenido. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-[5] bg-[image:linear-gradient(100deg,rgba(4,20,35,0.94)_0%,rgba(4,20,35,0.82)_38%,rgba(4,20,35,0.30)_66%,transparent_84%)]"
      />

      {/* Viñeta: cierra los cuatro bordes un punto y empuja la mirada al centro.
          Es lo que hace que la pantalla se lea como una escena con profundidad y
          no como un fondo plano con cosas encima. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-[4] bg-[image:radial-gradient(120%_100%_at_50%_50%,transparent_42%,rgba(2,12,22,0.55)_100%)]"
      />

      <div className="w-full max-w-[34rem] animate-entrada motion-reduce:animate-none">
        {children}
      </div>
    </main>
  );
}
